(() => {
  'use strict';

  const API_NAME = 'BMHHomeMotion';
  const VERSION = 19;
  const ROOT_CLASSES = ['bmh-motion-booting', 'bmh-motion-preparing', 'bmh-motion-ready'];
  const SECTION_SELECTOR = '[id^="shopify-section-"]';

  if (window[API_NAME]?.version === VERSION && typeof window[API_NAME].refresh === 'function') {
    window[API_NAME].refresh();
    return;
  }

  if (window[API_NAME]) {
    if (typeof window[API_NAME].destroy === 'function') window[API_NAME].destroy();
    else window[API_NAME].disable?.();
  }
  const lifecycle = new AbortController();
  const listen = (target, type, handler, options = {}) => target.addEventListener(type, handler, { ...options, signal: lifecycle.signal });

  const state = {
    observers: [],
    reveals: new Map(),
    pendingReveals: new Map(),
    activityObserver: null,
    activity: new Map(),
    saveData: Boolean(navigator.connection?.saveData),
    refreshFrame: 0,
    preparationTimer: 0,
    reduceMotion: window.matchMedia('(prefers-reduced-motion: reduce)'),
    mobileViewport: window.matchMedia('(max-width: 749px)')
  };

  const getConfig = () => {
    const configs = document.querySelectorAll('.bmh-home-motion-config[data-version="2"]');
    return configs.length ? configs[configs.length - 1] : null;
  };

  const readBoolean = (value) => value === 'true';

  const readNumber = (value, fallback, minimum = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.max(minimum, parsed) : fallback;
  };

  const readSettings = () => {
    const config = getConfig();
    if (!config) return null;

    const isMobile = state.mobileViewport.matches;
    const general = {
      duration: Math.min(isMobile ? 700 : 1200, readNumber(isMobile ? config.dataset.durationMobile : config.dataset.duration, isMobile ? 560 : 680, 1)),
      stagger: Math.min(90, readNumber(isMobile ? config.dataset.staggerMobile : config.dataset.stagger, isMobile ? 40 : 50)),
      distance: readNumber(isMobile ? config.dataset.distanceMobile : config.dataset.distance, isMobile ? 8 : 12),
      triggerPosition: readNumber(config.dataset.triggerPosition, 120)
    };

    const products = {
      duration: Math.min(isMobile ? 700 : 1200, readNumber(config.dataset.productDuration, general.duration, 1)),
      stagger: Math.min(90, readNumber(config.dataset.productStagger, general.stagger)),
      distance: readNumber(config.dataset.productDistance, general.distance),
      triggerPosition: readNumber(config.dataset.productTrigger, general.triggerPosition)
    };

    return {
      enabled: readBoolean(config.dataset.enabled),
      preset: config.dataset.preset || 'soft_visible',
      general,
      products,
      sections: {
        products: readBoolean(config.dataset.animateProducts),
        vacuum: readBoolean(config.dataset.animateVacuum),
        catalog: readBoolean(config.dataset.animateCatalog),
        about: readBoolean(config.dataset.animateAbout),
        reviews: readBoolean(config.dataset.animateReviews),
        faq: readBoolean(config.dataset.animateFaq)
      }
    };
  };

  const disconnectObservers = () => {
    state.observers.forEach((observer) => observer.disconnect());
    state.observers = [];
  };

  const unobserveTarget = (element) => {
    state.observers.forEach((observer) => observer.unobserve(element));
  };

  const removeRootClasses = () => {
    document.documentElement.classList.remove(...ROOT_CLASSES);
  };

  const cleanTarget = (element, markComplete = true) => {
    if (!element) return;
    state.pendingReveals.get(element)?.();
    state.reveals.get(element)?.();

    element.classList.remove('bmh-motion-target', 'is-bmh-motion-visible');
    if (markComplete) element.classList.add('bmh-motion-complete');
    element.removeAttribute('data-bmh-motion');
    element.removeAttribute('data-bmh-motion-duration');
    element.removeAttribute('data-bmh-motion-trigger');
    element.removeAttribute('data-bmh-motion-profile');
    element.style.removeProperty('--bmh-motion-delay');
    element.style.removeProperty('--bmh-motion-duration');
    element.style.removeProperty('--bmh-motion-distance');
  };

  const revealImmediately = (element) => {
    unobserveTarget(element);
    cleanTarget(element, true);
  };

  const reveal = (element) => {
    if (!element || element.classList.contains('is-bmh-motion-visible')) return;

    element.classList.add('is-bmh-motion-visible');
    unobserveTarget(element);

    const duration = Number.parseInt(element.dataset.bmhMotionDuration, 10) || 680;
    const delay = Number.parseInt(element.style.getPropertyValue('--bmh-motion-delay'), 10) || 0;
    const cleanupAfter = duration + delay + 180;
    let cleaned = false;
    let timer = 0;

    const finish = () => {
      if (cleaned) return;
      cleaned = true;
      element.removeEventListener('animationend', handleAnimationEnd);
      window.clearTimeout(timer);
      state.reveals.delete(element);
      cleanTarget(element, true);
    };

    const handleAnimationEnd = (event) => {
      if (
        event.target !== element ||
        (event.animationName !== 'bmhMotionReveal' && event.animationName !== 'bmhMotionFade')
      ) {
        return;
      }

      element.removeEventListener('animationend', handleAnimationEnd);
      finish();
    };

    element.addEventListener('animationend', handleAnimationEnd);
    state.reveals.set(element, finish);
    timer = window.setTimeout(finish, cleanupAfter);
  };

  const canRegister = (element) => {
    if (
      !element ||
      element.classList.contains('bmh-motion-complete') ||
      element.matches('script, style, template, iframe, video') ||
      element.hidden ||
      element.closest('[hidden], [data-bmh-motion-skip]')
    ) {
      return false;
    }

    return true;
  };

  // Reveal the whole horizontal row together so a swipe never exposes an invisible card.
  const revealEntry = (element, immediate = false) => {
    const row = element.closest('.resource-list__carousel');
    const targets = row ? row.querySelectorAll('.bmh-motion-target') : [element];
    targets.forEach((target) => {
      if (immediate) revealImmediately(target);
      else reveal(target);
    });
  };

  const recoverVisibleTargets = () => {
    if (document.hidden) return;
    const visible = Array.from(document.querySelectorAll('.bmh-motion-target')).filter((element) => {
      const bounds = element.getBoundingClientRect();
      return bounds.bottom > 0 && bounds.top < window.innerHeight;
    });
    visible.forEach((element) => revealEntry(element, true));
  };

  const register = (element, kind, order, profile, profileName, seen) => {
    if (!canRegister(element) || seen.has(element)) return false;

    seen.add(element);
    element.classList.add('bmh-motion-target');
    element.dataset.bmhMotion = kind;
    element.dataset.bmhMotionDuration = String(profile.duration);
    element.dataset.bmhMotionTrigger = String(profile.triggerPosition);
    element.dataset.bmhMotionProfile = profileName;

    const staggerIndex = Math.min(Math.max(order, 0), 5);
    element.style.setProperty('--bmh-motion-duration', String(profile.duration) + 'ms');
    element.style.setProperty('--bmh-motion-distance', String(profile.distance) + 'px');
    element.style.setProperty('--bmh-motion-delay', String(Math.min(staggerIndex * profile.stagger, 360)) + 'ms');
    return true;
  };

  const closestSection = (element) => element?.closest(SECTION_SELECTOR) || null;

  const findSectionByDescendant = (main, selector) => closestSection(main.querySelector(selector));

  const findSectionBySuffix = (main, suffix) =>
    Array.from(main.querySelectorAll(SECTION_SELECTOR)).find((section) => section.id.endsWith(suffix)) || null;

  const clearSectionTargets = (section) => {
    if (!section) return;
    section.querySelectorAll('.bmh-motion-target').forEach((element) => cleanTarget(element, false));
  };

  const addProducts = (main, settings, seen) => {
    main.querySelectorAll('[data-testid="product-list"]').forEach((list) => {
      const section = closestSection(list);
      if (!settings.sections.products) { clearSectionTargets(section); return; }
      register(section.querySelector('.section-resource-list__header'), 'text', 0, settings.products, 'products', seen);
      Array.from(section.querySelectorAll('.resource-list__item')).forEach((item, index) => {
        register(item, 'card', index % 4, settings.products, 'products', seen);
      });
    });
  };

  const addCommunity = (main, settings, seen) => {
    const section = main.querySelector('.bmh-community');
    if (!section) return;
    register(section.querySelector('.bmh-community__header'), 'text', 0, settings.general, 'general', seen);
    // The reel component owns its entrance; never hide the playing track on scroll.
    section.querySelectorAll('.bmh-community__profile').forEach((card, index) => register(card, 'card', index, settings.general, 'general', seen));
  };

  const addVacuum = (main, settings, seen) => {
    const section = findSectionByDescendant(main, '.bmh-home-combo');
    if (!section) return;

    if (!settings.sections.vacuum) {
      clearSectionTargets(section);
      return;
    }

    register(section.querySelector('.bmh-home-combo__media'), 'media', 0, settings.general, 'general', seen);
    register(section.querySelector('.bmh-home-combo__content'), 'soft', 1, settings.general, 'general', seen);
  };

  const addCatalog = (main, settings, seen) => {
    const section = findSectionByDescendant(main, '.bmh-catalog');
    if (!section) return;

    if (!settings.sections.catalog) {
      clearSectionTargets(section);
      return;
    }

    register(section.querySelector('.bmh-catalog__content'), 'text', 0, settings.general, 'general', seen);

    Array.from(section.querySelectorAll('.bmh-catalog__card')).slice(0, 3).forEach((card, index) => {
      register(card, 'card', index + 1, settings.general, 'general', seen);
    });
  };

  const addAbout = (main, settings, seen) => {
    const section = findSectionByDescendant(main, '[data-testid="media-with-content"]');
    if (!section) return;

    if (!settings.sections.about) {
      clearSectionTargets(section);
      return;
    }

    register(section.querySelector('.media-with-content > .media-block'), 'media', 0, settings.general, 'general', seen);
    register(section.querySelector('.media-with-content > .media-with-content__content'), 'soft', 1, settings.general, 'general', seen);
  };

  const addReviews = (main, settings, seen) => {
    const section = findSectionByDescendant(main, '.bmh-home-reviews');
    if (!section) return;

    if (!settings.sections.reviews) {
      clearSectionTargets(section);
      return;
    }

    const wrapper = section.querySelector('.bmh-home-reviews__header');
    register(wrapper, 'fade', 0, settings.general, 'general', seen);
    section.querySelectorAll('.bmh-home-reviews__metrics > div').forEach((metric, index) => register(metric, 'card', index, settings.general, 'general', seen));
  };

  const addFaq = (main, settings, seen) => {
    const section = findSectionBySuffix(main, '__section_zN9ncP');
    if (!section) return;

    if (!settings.sections.faq) {
      clearSectionTargets(section);
      return;
    }

    register(section.querySelector('.section-content-wrapper h2, .section-content-wrapper h3'), 'text', 0, settings.general, 'general', seen);
    register(section.querySelector('.section-content-wrapper .accordion'), 'fade', 1, settings.general, 'general', seen);
  };

  const buildChoreography = (main, settings) => {
    const seen = new Set();

    addProducts(main, settings, seen);
    addCommunity(main, settings, seen);
    addVacuum(main, settings, seen);
    addCatalog(main, settings, seen);
    addAbout(main, settings, seen);
    addReviews(main, settings, seen);
    addFaq(main, settings, seen);
  };

  const disable = () => {
    window.clearTimeout(state.preparationTimer);
    window.cancelAnimationFrame(state.refreshFrame);

    disconnectObservers();

    document.querySelectorAll('.bmh-motion-target').forEach((element) => cleanTarget(element, false));
    removeRootClasses();
  };

  const updateActivity = () => {
    state.activity.forEach((visible, element) => {
      element.classList.toggle('bmh-motion-paused', !visible || document.hidden || state.reduceMotion.matches || state.saveData);
    });
  };

  const setupActivity = (main) => {
    state.activityObserver?.disconnect();
    state.activity.forEach((_, element) => element.classList.remove('bmh-motion-paused'));
    state.activity.clear();
    document.documentElement.classList.toggle('bmh-home-static', state.saveData || state.reduceMotion.matches);
    const elements = main.querySelectorAll('marquee-component, [role="region"][class^="ai-premium-ticker-"], .bmh-home-closing');
    if ('IntersectionObserver' in window) {
      state.activityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => state.activity.set(entry.target, entry.isIntersecting));
        updateActivity();
      });
    }
    elements.forEach((element) => {
      state.activity.set(element, false);
      state.activityObserver?.observe(element);
    });
    updateActivity();
  };

  const scan = () => {
    try {
      const main = document.querySelector('main');
      const settings = readSettings();

      if (!main || !settings || !settings.enabled) {
        disable();
        return;
      }

      disconnectObservers();

      removeRootClasses();
      document.documentElement.classList.add('bmh-motion-preparing');

      window.clearTimeout(state.preparationTimer);
      state.preparationTimer = window.setTimeout(() => {
        if (document.documentElement.classList.contains('bmh-motion-preparing')) disable();
      }, 1600);

      buildChoreography(main, settings);

      const targets = main.querySelectorAll('.bmh-motion-target:not(.is-bmh-motion-visible)');

      setupActivity(main);
      if (state.reduceMotion.matches || state.saveData || document.documentElement.hasAttribute('data-bmh-basic') || !('IntersectionObserver' in window)) {
        targets.forEach(revealImmediately);
        window.clearTimeout(state.preparationTimer);
        document.documentElement.classList.remove('bmh-motion-preparing');
        document.documentElement.classList.add('bmh-motion-ready');
        return;
      }

      const targetGroups = new Map();

      // Read all positions before revealing any target to avoid repeated layout flushes.
      const positions = Array.from(targets, (element) => ({element, bounds: element.getBoundingClientRect()}));
      positions.forEach(({element, bounds}) => {
        // Keep restored scroll positions and already-passed content visible.
        if (bounds.bottom <= 0 || bounds.top < window.innerHeight) {
          revealEntry(element, true);
          return;
        }
        const requestedTrigger = readNumber(element.dataset.bmhMotionTrigger, settings.general.triggerPosition);
        const trigger = Math.min(requestedTrigger, Math.max(window.innerHeight - 80, 0));
        const key = String(trigger);
        if (!targetGroups.has(key)) targetGroups.set(key, []);
        targetGroups.get(key).push(element);
      });

      state.refreshFrame = window.requestAnimationFrame(() => {
        window.clearTimeout(state.preparationTimer);
        document.documentElement.classList.remove('bmh-motion-preparing');
        document.documentElement.classList.add('bmh-motion-ready');

        targetGroups.forEach((elements, trigger) => {
          const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) revealEntry(entry.target);
            });
          }, {
            root: null,
            rootMargin: '0px 0px -' + Number(trigger) + 'px 0px',
            threshold: 0.01
          });

          state.observers.push(observer);
          elements.forEach((element) => {
            if (element.classList.contains('bmh-motion-target')) observer.observe(element);
          });
        });
      });
    } catch (error) {
      console.warn('[BeMyHome Motion] Animations disabled safely.', error);
      disable();
    }
  };

  const refresh = () => {
    window.cancelAnimationFrame(state.refreshFrame);
    state.refreshFrame = window.requestAnimationFrame(scan);
  };

  const handleReducedMotion = () => {
    if (state.reduceMotion.matches) disable();
    else refresh();
    updateActivity();
  };

  const handleFocusIn = (event) => {
    const target = event.target.closest?.('.bmh-motion-target');
    if (!event.target.matches?.(':focus-visible')) return;
    if (target) revealImmediately(target);
  };

  listen(document, 'focusin', handleFocusIn);
  listen(document, 'visibilitychange', () => {
    updateActivity();
    recoverVisibleTargets();
  });
  listen(document, 'shopify:section:load', refresh);
  listen(document, 'shopify:section:reorder', refresh);
  listen(document, 'shopify:section:unload', (event) => {
    state.reveals.forEach((finish, element) => { if (event.target.contains(element)) finish(); });
    refresh();
  });
  listen(window, 'pageshow', (event) => {
    if (event.persisted) refresh();
    recoverVisibleTargets();
  });

  if (state.reduceMotion.addEventListener) {
    listen(state.reduceMotion, 'change', handleReducedMotion);
    listen(state.mobileViewport, 'change', refresh);
  } else {
    state.reduceMotion.addListener(handleReducedMotion);
    state.mobileViewport.addListener(refresh);
  }

  window[API_NAME] = {
    version: VERSION,
    refresh,
    disable,
    destroy() {
      disable();
      lifecycle.abort();
      state.activityObserver?.disconnect();
      state.activity.forEach((_, element) => element.classList.remove('bmh-motion-paused'));
      state.activity.clear();
      state.reveals.forEach((finish) => finish());
      state.reduceMotion.removeListener?.(handleReducedMotion);
      state.mobileViewport.removeListener?.(refresh);
      document.documentElement.classList.remove('bmh-home-static');
    }
  };

  if (document.readyState === 'loading') {
    listen(document, 'DOMContentLoaded', refresh, { once: true });
  } else {
    refresh();
  }
})();


